import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from './ThemeProvider';
import { menuStructure, actions, Icon, ROLE_LEVEL } from '../data/menuStructure';

function fuzzyScore(query, text) {
  if (!query) return 1;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  if (t === q) return 100;
  if (t.startsWith(q)) return 80 + (1 - q.length / t.length) * 20;
  if (t.includes(q)) return 50 + (1 - q.length / t.length) * 30;
  const tokens = q.split(/\s+/).filter(Boolean);
  const allMatch = tokens.every(token => t.includes(token));
  if (allMatch) return 20 + tokens.length * 5;
  return -1;
}

function searchItems(query, flatItems, recentIds) {
  if (!query.trim()) {
    return { pages: [], actions: [], recent: [] };
  }

  const q = query.toLowerCase().trim();
  const pageResults = [];
  const actionResults = [];

  for (const item of flatItems) {
    const fields = [item.label, item.category || '', ...(item.keywords || [])];
    let bestScore = -1;
    for (const f of fields) {
      const s = fuzzyScore(q, f);
      if (s > bestScore) bestScore = s;
    }
    if (bestScore >= 0) {
      pageResults.push({ ...item, score: bestScore });
    }
  }

  for (const act of actions) {
    const fields = [act.label, ...(act.keywords || [])];
    let bestScore = -1;
    for (const f of fields) {
      const s = fuzzyScore(q, f);
      if (s > bestScore) bestScore = s;
    }
    if (bestScore >= 0) {
      actionResults.push({ ...act, score: bestScore });
    }
  }

  pageResults.sort((a, b) => b.score - a.score);
  actionResults.sort((a, b) => b.score - a.score);

  const recentItems = recentIds
    .map(id => flatItems.find(item => item.id === id))
    .filter(Boolean)
    .filter(item => {
      const fields = [item.label, item.category || '', ...(item.keywords || [])];
      return fields.some(f => fuzzyScore(q, f) >= 0);
    })
    .slice(0, 5);

  return {
    pages: pageResults.slice(0, 8),
    actions: actionResults.slice(0, 4),
    recent: recentItems,
  };
}

function flattenMenu(menu, userLevel) {
  const items = [];
  for (const group of menu) {
    for (const item of group.items) {
      if (item.hideIf && item.hideIf(userLevel)) continue;
      if (userLevel < (ROLE_LEVEL[item.minRole] ?? 99)) continue;
      items.push({ ...item, category: group.category });
      if (item.children) {
        for (const child of item.children) {
          if (userLevel < (ROLE_LEVEL[child.minRole] ?? 99)) continue;
          items.push({ ...child, category: group.category + ' → ' + item.label });
        }
      }
    }
  }
  return items;
}

function HighlightedText({ text, query }) {
  if (!query) return <span>{text}</span>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, idx)}
      <span style={{ color: 'var(--accent-1)', fontWeight: 600 }}>
        {text.slice(idx, idx + query.length)}
      </span>
      {text.slice(idx + query.length)}
    </span>
  );
}

function CommandPalette({ isOpen, onClose, onNavigate, userRole, recentIds }) {
  const { theme } = useTheme();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const overlayRef = useRef(null);

  const userLevel = ROLE_LEVEL[userRole] ?? -1;
  const flatItems = useMemo(() => flattenMenu(menuStructure, userLevel), [userLevel]);

  const results = useMemo(() =>
    searchItems(query, flatItems, recentIds || [])
  , [query, flatItems, recentIds]);

  const allResults = useMemo(() => {
    const arr = [];
    if (results.recent.length > 0) {
      arr.push({ type: 'heading', label: 'Recent' });
      for (const r of results.recent) arr.push({ type: 'page', ...r });
    }
    if (results.pages.length > 0) {
      arr.push({ type: 'heading', label: 'Pages' });
      for (const p of results.pages) arr.push({ type: 'page', ...p });
    }
    if (results.actions.length > 0) {
      arr.push({ type: 'heading', label: 'Actions' });
      for (const a of results.actions) arr.push({ type: 'action', ...a });
    }
    return arr;
  }, [results]);

  const selectableItems = allResults.filter(r => r.type !== 'heading');

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const items = el.querySelectorAll('[data-palette-item]');
    if (items[selectedIndex]) {
      items[selectedIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(idx => Math.min(idx + 1, selectableItems.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(idx => Math.max(idx - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const item = selectableItems[selectedIndex];
        if (!item) return;
        handleSelect(item);
        return;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, selectableItems]);

  const handleSelect = (item) => {
    if (item.type === 'page') {
      onNavigate(item.id);
    } else if (item.type === 'action') {
      const hash = item.page + (item.param || '');
      onNavigate(item.id, hash);
    }
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  if (!isOpen) return null;

  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const modKey = isMac ? '⌘' : 'Ctrl';

  const renderItem = (item, idx) => {
    if (item.type === 'heading') {
      return (
        <div key={item.type + '-' + item.label + '-' + idx}
          className="uppercase tracking-wider"
          style={{ padding: '16px 16px 4px', fontSize: 10, fontWeight: 600, color: 'var(--text-3)' }}>
          {item.label}
        </div>
      );
    }
    const si = selectableItems.indexOf(item);
    const isSel = si === selectedIndex;
    return (
      <button
        key={(item.type === 'action' ? 'act-' : 'page-') + item.id + '-' + idx}
        data-palette-item
        onClick={() => handleSelect(item)}
        className="flex items-center w-full text-left"
        style={{ padding: '10px 16px', fontSize: 14, background: isSel ? 'rgba(139,92,246,0.14)' : 'transparent', color: isSel ? 'var(--text-1)' : 'var(--text-2)' }}
      >
        <span className="flex-shrink-0" style={{ display: 'inline-flex', color: 'var(--text-3)' }}>
          <Icon name={item.icon} className="w-5 h-5" />
        </span>
        <span className="flex-1 font-medium" style={{ marginLeft: 12 }}>
          <HighlightedText text={item.label} query={query} />
        </span>
        {item.category && item.type === 'page' && (
          <span className="truncate" style={{ marginLeft: 8, fontSize: 10, color: 'var(--text-3)', maxWidth: 100 }}>
            {item.category}
          </span>
        )}
        {item.type === 'action' && (
          <span className="uppercase" style={{ marginLeft: 8, fontSize: 10, color: 'var(--warning)', fontWeight: 500 }}>Action</span>
        )}
        {item.type === 'page' && recentIds?.includes(item.id) && (
          <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--text-3)' }}>recent</span>
        )}
      </button>
    );
  };

  const noResults = allResults.length === 0 && query.trim().length > 0;

  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[110] flex items-start justify-center"
      style={{ paddingTop: '15vh', background: 'rgba(5,6,15,0.6)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
    >
      <div className="w-full max-w-xl overflow-hidden" style={{ background: 'var(--surface)', borderRadius: 12, boxShadow: 'var(--shadow-2)', border: '1px solid var(--glass-border)' }}>
        <div className="flex items-center gap-3" style={{ padding: '12px 16px', borderBottom: '1px solid var(--glass-border)' }}>
          <span className="flex-shrink-0" style={{ display: 'inline-flex', color: 'var(--text-3)' }}>
            <Icon name="search" className="w-5 h-5" />
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages and actions..."
            className="flex-1 outline-none"
            style={{ background: 'transparent', border: 'none', fontSize: 14, color: 'var(--text-1)' }}
            spellCheck={false}
            autoComplete="off"
          />
          <kbd className="hidden sm:inline-flex items-center gap-0.5" style={{ padding: '2px 8px', borderRadius: 6, background: 'var(--bg-3)', fontSize: 10, fontWeight: 500, color: 'var(--text-2)', border: '1px solid var(--glass-border)' }}>
            <span>{modKey}</span><span>K</span>
          </kbd>
          <button onClick={onClose} style={{ padding: 4, borderRadius: 6, color: 'var(--text-3)', display: 'inline-flex' }}>
            <Icon name="x" className="w-4 h-4" />
          </button>
        </div>

        <div ref={listRef} className="overflow-y-auto" style={{ maxHeight: 320, padding: '4px 0' }}>
          {noResults && (
            <div className="text-center" style={{ padding: '16px 16px 32px', fontSize: 14, color: 'var(--text-3)' }}>
              No results found for "<span style={{ color: 'var(--text-2)' }}>{query}</span>"
            </div>
          )}

          {!query.trim() && !noResults && (
            <>
              {recentIds && recentIds.length > 0 && (
                <>
                  <div className="uppercase tracking-wider" style={{ padding: '12px 16px 4px', fontSize: 10, fontWeight: 600, color: 'var(--text-3)' }}>Recent</div>
                  {recentIds.slice(0, 5).map(id => {
                    const item = flatItems.find(fi => fi.id === id);
                    if (!item) return null;
                    return (
                      <button
                        key={'recent-' + id}
                        data-palette-item
                        onClick={() => { onNavigate(item.id); onClose(); }}
                        className="flex items-center w-full text-left"
                        style={{ padding: '10px 16px', fontSize: 14, color: 'var(--text-2)' }}
                      >
                        <span className="flex-shrink-0" style={{ display: 'inline-flex', color: 'var(--text-3)' }}>
                          <Icon name={item.icon} className="w-5 h-5" />
                        </span>
                        <span className="flex-1 font-medium" style={{ marginLeft: 12 }}>{item.label}</span>
                        <span className="truncate" style={{ marginLeft: 8, fontSize: 10, color: 'var(--text-3)', maxWidth: 100 }}>{item.category}</span>
                      </button>
                    );
                  })}
                </>
              )}
              <div className="uppercase tracking-wider" style={{ padding: '12px 16px 4px', fontSize: 10, fontWeight: 600, color: 'var(--text-3)' }}>Quick Actions</div>
              {actions.filter(() => true).slice(0, 4).map((act) => (
                <button
                  key={act.id}
                  data-palette-item
                  onClick={() => { const hash = act.page + (act.param || ''); onNavigate(act.id, hash); onClose(); }}
                  className="flex items-center w-full text-left"
                  style={{ padding: '10px 16px', fontSize: 14, color: 'var(--text-2)' }}
                >
                  <span className="flex-shrink-0" style={{ display: 'inline-flex', color: 'var(--text-3)' }}>
                    <Icon name={act.icon} className="w-5 h-5" />
                  </span>
                  <span className="flex-1 font-medium" style={{ marginLeft: 12 }}>{act.label}</span>
                  <span className="uppercase" style={{ marginLeft: 8, fontSize: 10, color: 'var(--warning)', fontWeight: 500 }}>Action</span>
                </button>
              ))}
              <div style={{ padding: '12px 16px 8px', fontSize: 10, color: 'var(--text-3)' }}>
                Start typing to search all pages, or use ↑↓ to navigate
              </div>
            </>
          )}

          {query.trim() && allResults.map((item, idx) => renderItem(item, idx))}
        </div>

        <div className="flex items-center gap-4" style={{ padding: '8px 16px', borderTop: '1px solid var(--glass-border)', fontSize: 10, color: 'var(--text-3)' }}>
          <span className="flex items-center gap-1"><kbd style={{ padding: '1px 4px', borderRadius: 4, background: 'var(--bg-3)', color: 'var(--text-2)', border: '1px solid var(--glass-border)' }}>↑↓</kbd> Navigate</span>
          <span className="flex items-center gap-1"><kbd style={{ padding: '1px 4px', borderRadius: 4, background: 'var(--bg-3)', color: 'var(--text-2)', border: '1px solid var(--glass-border)' }}>↵</kbd> Select</span>
          <span className="flex items-center gap-1"><kbd style={{ padding: '1px 4px', borderRadius: 4, background: 'var(--bg-3)', color: 'var(--text-2)', border: '1px solid var(--glass-border)' }}>Esc</kbd> Close</span>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default CommandPalette;
